import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useSeason } from "@/lib/SeasonContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import { CalendarPlus, Trash2, Upload, ClipboardList, Check, X, Clock, AlertCircle, ChevronRight, Save } from "lucide-react";
import { toast } from "sonner";

const SESSION_TYPES = [
  { value: "practice",  label: "Practice",   color: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  { value: "film",      label: "Film Room",  color: "text-blue-300    border-blue-500/40    bg-blue-500/10" },
  { value: "weights",   label: "Weights",    color: "text-orange-300  border-orange-500/40  bg-orange-500/10" },
  { value: "game_day",  label: "Game Day",   color: "text-burgundy    border-burgundy/60    bg-burgundy/15" },
  { value: "off",       label: "Off Day",    color: "text-ink-300     border-ink-600         bg-ink-800" },
];
const TYPE_MAP = Object.fromEntries(SESSION_TYPES.map((t) => [t.value, t]));

const STATUS_BUTTONS = [
  { value: "present", label: "Present", icon: Check,       activeCls: "bg-emerald-500 text-white border-emerald-500" },
  { value: "late",    label: "Late",    icon: Clock,       activeCls: "bg-amber-500 text-white border-amber-500" },
  { value: "excused", label: "Excused", icon: AlertCircle, activeCls: "bg-blue-500 text-white border-blue-500" },
  { value: "absent",  label: "Absent",  icon: X,           activeCls: "bg-red-500 text-white border-red-500" },
];

function today() { return new Date().toISOString().slice(0, 10); }
function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); }
  catch { return d; }
}

export default function Practice() {
  const { activeSeason } = useSeason();
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [session, setSession] = useState(null);
  const [roster, setRoster] = useState([]); // [{player_id, player, status, note}]
  const [overview, setOverview] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newDate, setNewDate] = useState(today());
  const [newType, setNewType] = useState("practice");
  const [newNotes, setNewNotes] = useState("");
  const uploadRef = useRef(null);

  // Load sessions + overview whenever the active season changes
  const loadSessions = async () => {
    try {
      const [s, ov] = await Promise.all([
        api.get("/practice-sessions", { params: activeSeason ? { season: activeSeason } : {} }),
        api.get("/practice-attendance/overview", { params: activeSeason ? { season: activeSeason } : {} }),
      ]);
      setSessions(s.data);
      setOverview(ov.data);
      // Auto-select most recent session if nothing selected or the selected one disappeared
      if (s.data.length > 0) {
        if (!selectedId || !s.data.find((x) => x.id === selectedId)) {
          setSelectedId(s.data[0].id);
        }
      } else {
        setSelectedId(null);
        setSession(null);
        setRoster([]);
      }
    } catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { loadSessions(); }, [activeSeason]);

  // Load detail whenever selectedId changes
  useEffect(() => {
    if (!selectedId) return;
    (async () => {
      try {
        const { data } = await api.get(`/practice-sessions/${selectedId}/attendance`);
        setSession(data.session);
        setRoster(data.roster);
        setDirty(false);
      } catch (e) { toast.error(formatError(e)); }
    })();
  }, [selectedId]);

  const setStatus = (playerId, status) => {
    setRoster((r) => r.map((row) => row.player_id === playerId ? { ...row, status } : row));
    setDirty(true);
  };
  const setNote = (playerId, note) => {
    setRoster((r) => r.map((row) => row.player_id === playerId ? { ...row, note } : row));
    setDirty(true);
  };
  const markAll = (status) => {
    setRoster((r) => r.map((row) => ({ ...row, status })));
    setDirty(true);
    toast.info(`Marked all as ${status}`);
  };

  const save = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const records = roster
        .filter((r) => r.status)  // only save rows the coach has touched
        .map((r) => ({ player_id: r.player_id, status: r.status, note: r.note || null }));
      const { data } = await api.post(`/practice-sessions/${selectedId}/attendance`, { records });
      toast.success(`Saved · ${data.saved} record${data.saved === 1 ? "" : "s"}`);
      setDirty(false);
      loadSessions();  // refresh session counts + overview
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const createSession = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/practice-sessions", { date: newDate, session_type: newType, notes: newNotes });
      toast.success(`${TYPE_MAP[newType]?.label} added for ${fmtDate(newDate)}`);
      setNewDialogOpen(false); setNewNotes("");
      await loadSessions();
      setSelectedId(data.id);
    } catch (e) { toast.error(formatError(e)); }
  };

  const removeSession = async () => {
    if (!selectedId) return;
    if (!confirm(`Delete this ${session?.session_type || "session"} on ${fmtDate(session?.date)}? Attendance records will be deleted too.`)) return;
    try {
      await api.delete(`/practice-sessions/${selectedId}`);
      toast.success("Session deleted");
      setSelectedId(null);
      await loadSessions();
    } catch (e) { toast.error(formatError(e)); }
  };

  const updateSessionMeta = async (patch) => {
    if (!selectedId) return;
    try {
      const { data } = await api.patch(`/practice-sessions/${selectedId}`, patch);
      setSession(data);
      loadSessions();
    } catch (e) { toast.error(formatError(e)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/practice-attendance/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.records_saved === 0) {
        toast.error(`Nothing imported. Format: first column = player name, subsequent column headers = dates (with X or blank in each cell).`, { duration: 8000 });
      } else {
        const msg = `Imported ${data.records_saved} records across ${data.sessions_created} new + ${data.sessions_existing} existing sessions (${data.players_matched} players matched)`;
        if (data.unmatched_names?.length) {
          toast.warning(`${msg}. Unmatched names: ${data.unmatched_names.slice(0, 5).join(", ")}${data.unmatched_names.length > 5 ? "…" : ""}`, { duration: 10000 });
        } else {
          toast.success(msg);
        }
      }
      loadSessions();
    } catch (err) { toast.error(formatError(err)); }
    finally { e.target.value = ""; }
  };

  // Group sessions by month for the sidebar list
  const groupedSessions = useMemo(() => {
    const groups = {};
    for (const s of sessions) {
      const key = (s.date || "").slice(0, 7); // YYYY-MM
      (groups[key] ||= []).push(s);
    }
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [sessions]);

  const rosterPresent = roster.filter((r) => r.status === "present").length;
  const rosterLate    = roster.filter((r) => r.status === "late").length;
  const rosterExcused = roster.filter((r) => r.status === "excused").length;
  const rosterAbsent  = roster.filter((r) => r.status === "absent").length;
  const rosterUnset   = roster.filter((r) => !r.status).length;

  return (
    <div className="p-6 md:p-10" data-testid="practice-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <p className="eyebrow">Coach Tools</p>
          <h1 className="heading-1">Practice <span className="text-burgundy">Attendance</span></h1>
          {activeSeason && (
            <div className="mt-3 inline-flex items-center gap-2 bg-burgundy/15 border border-burgundy/50 rounded-md px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-ink-300 font-bold">Season</span>
              <span className="font-display font-black text-white uppercase tracking-wider text-sm">{activeSeason}</span>
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="border-ink-700 hover:bg-ink-800"
                  onClick={() => uploadRef.current?.click()}
                  title="Import a grid: player names in first column, dates across subsequent columns, X = present."
                  data-testid="attendance-upload-btn">
            <Upload className="w-4 h-4 mr-2" /> Upload Attendance
          </Button>
          <input ref={uploadRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={upload} />
          <Button className="bg-burgundy hover:bg-burgundy-hover" onClick={() => { setNewDate(today()); setNewType("practice"); setNewNotes(""); setNewDialogOpen(true); }} data-testid="new-session-btn">
            <CalendarPlus className="w-4 h-4 mr-2" /> New Session
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        {/* LEFT: Session list */}
        <div className="bg-ink-800 rounded-lg border border-white/5 max-h-[75vh] overflow-y-auto">
          {sessions.length === 0 ? (
            <div className="p-8 text-center text-ink-300">
              <ClipboardList className="w-8 h-8 mx-auto mb-3 text-ink-500" />
              <p className="text-sm">No sessions yet. Hit <span className="text-white font-semibold">New Session</span> to log today&apos;s practice.</p>
            </div>
          ) : (
            groupedSessions.map(([month, monthSessions]) => (
              <div key={month} className="border-b border-ink-700/40 last:border-0">
                <p className="eyebrow px-4 pt-4 pb-2 sticky top-0 bg-ink-800 z-10">{month}</p>
                {monthSessions.map((s) => {
                  const type = TYPE_MAP[s.session_type] || TYPE_MAP.practice;
                  const active = s.id === selectedId;
                  const totalMarked = (s.counts?.present || 0) + (s.counts?.absent || 0) + (s.counts?.late || 0) + (s.counts?.excused || 0);
                  return (
                    <button
                      key={s.id}
                      onClick={() => setSelectedId(s.id)}
                      className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-ink-700/40 transition ${active ? "bg-burgundy/15 border-l-2 border-burgundy" : "border-l-2 border-transparent"}`}
                      data-testid={`session-row-${s.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white truncate">{fmtDate(s.date)}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className={`inline-flex items-center text-[9px] uppercase tracking-wider font-bold border rounded px-1.5 py-0.5 ${type.color}`}>{type.label}</span>
                          {totalMarked > 0 && (
                            <span className="text-[10px] text-ink-400">{s.counts.present + s.counts.late}/{totalMarked} in</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className={`w-4 h-4 shrink-0 ${active ? "text-burgundy" : "text-ink-500"}`} />
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* RIGHT: Detail */}
        <div className="min-w-0">
          {!session ? (
            <div className="bg-ink-800 rounded-lg border border-white/5 p-10 text-center text-ink-300">
              Pick a session on the left to mark attendance.
            </div>
          ) : (
            <>
              {/* Session header */}
              <div className="bg-ink-800 rounded-lg border border-white/5 p-5 mb-4">
                <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
                  <div>
                    <p className="eyebrow">Session</p>
                    <h2 className="font-display text-3xl font-black uppercase tracking-tight">{fmtDate(session.date)}</h2>
                  </div>
                  <div className="flex gap-2 items-center flex-wrap">
                    <Select value={session.session_type} onValueChange={(v) => updateSessionMeta({ session_type: v })}>
                      <SelectTrigger className="w-[150px] bg-ink-900 border-ink-700 text-white h-9" data-testid="session-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-ink-900 border-ink-700">
                        {SESSION_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <button onClick={removeSession} className="text-ink-400 hover:text-red-400 border border-ink-700 hover:border-red-500/40 rounded p-2 transition" title="Delete session" data-testid="delete-session-btn">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="eyebrow">Notes</Label>
                  <Textarea
                    value={session.notes || ""}
                    onChange={(e) => setSession({ ...session, notes: e.target.value })}
                    onBlur={(e) => updateSessionMeta({ notes: e.target.value })}
                    placeholder="Optional notes about this session…"
                    className="mt-2 bg-ink-900 border-ink-700 min-h-[60px]"
                    data-testid="session-notes"
                  />
                </div>

                {/* Summary pills */}
                <div className="flex flex-wrap gap-2 mt-4">
                  <SummaryPill label="Present" value={rosterPresent} color="emerald" />
                  <SummaryPill label="Late" value={rosterLate} color="amber" />
                  <SummaryPill label="Excused" value={rosterExcused} color="blue" />
                  <SummaryPill label="Absent" value={rosterAbsent} color="red" />
                  {rosterUnset > 0 && <SummaryPill label="Untracked" value={rosterUnset} color="ink" />}
                </div>
              </div>

              {/* Quick-mark buttons + Save */}
              <div className="flex flex-wrap gap-2 mb-4 items-center">
                <span className="text-[10px] uppercase tracking-wider text-ink-400 font-bold mr-2">Mark all:</span>
                {STATUS_BUTTONS.map((b) => (
                  <button
                    key={b.value}
                    onClick={() => markAll(b.value)}
                    className="text-xs uppercase tracking-wider border border-ink-700 hover:bg-ink-800 text-ink-200 hover:text-white rounded px-2.5 py-1.5 transition"
                    data-testid={`mark-all-${b.value}`}
                  >
                    <b.icon className="w-3 h-3 inline mr-1" /> {b.label}
                  </button>
                ))}
                <div className="flex-1" />
                <Button
                  onClick={save}
                  disabled={saving || !dirty}
                  className={dirty ? "bg-burgundy hover:bg-burgundy-hover" : "bg-ink-700 hover:bg-ink-600"}
                  data-testid="save-attendance-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving…" : dirty ? "Save Attendance" : "All Saved"}
                </Button>
              </div>

              {/* Roster grid */}
              <div className="bg-ink-800 rounded-lg border border-white/5 divide-y divide-ink-700/40">
                {roster.map((row) => {
                  const overviewRow = overview?.players?.find((p) => p.player.id === row.player_id);
                  return (
                    <div key={row.player_id} className="p-4 grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-4 items-center" data-testid={`attendance-row-${row.player_id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <PlayerAvatar player={row.player} size="sm" />
                        <div className="min-w-0">
                          <p className="font-display font-bold uppercase text-white text-sm truncate">{row.player.player_name}</p>
                          <p className="text-[10px] text-ink-400 uppercase tracking-wider">#{row.player.jersey_number || "—"} · {row.player.position || "—"}
                            {overviewRow?.attendance_pct != null && (
                              <span className="ml-2 text-emerald-300 font-bold">{overviewRow.attendance_pct}%</span>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1.5">
                        {STATUS_BUTTONS.map((b) => {
                          const active = row.status === b.value;
                          return (
                            <button
                              key={b.value}
                              onClick={() => setStatus(row.player_id, b.value)}
                              className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider rounded px-2.5 py-1.5 border transition ${
                                active ? b.activeCls : "border-ink-700 text-ink-300 hover:bg-ink-700 hover:text-white"
                              }`}
                              data-testid={`status-${row.player_id}-${b.value}`}
                            >
                              <b.icon className="w-3 h-3" /> {b.label}
                            </button>
                          );
                        })}
                      </div>

                      <Input
                        value={row.note || ""}
                        onChange={(e) => setNote(row.player_id, e.target.value)}
                        placeholder="Note (optional)"
                        className="bg-ink-900 border-ink-700 text-sm w-full md:w-48"
                        data-testid={`note-${row.player_id}`}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* New session dialog */}
      <Dialog open={newDialogOpen} onOpenChange={setNewDialogOpen}>
        <DialogContent className="bg-ink-900 border-ink-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="heading-3 flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-burgundy" /> New Session
            </DialogTitle>
            <DialogDescription className="text-ink-300">Log a new practice, film session, weight room, game day, or off day.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createSession} className="space-y-4" data-testid="new-session-form">
            <div>
              <Label className="eyebrow">Date</Label>
              <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="bg-ink-800 border-ink-700 mt-2" required data-testid="new-session-date" />
            </div>
            <div>
              <Label className="eyebrow">Type</Label>
              <Select value={newType} onValueChange={setNewType}>
                <SelectTrigger className="bg-ink-800 border-ink-700 mt-2" data-testid="new-session-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-ink-900 border-ink-700">
                  {SESSION_TYPES.map((t) => (<SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow">Notes</Label>
              <Textarea value={newNotes} onChange={(e) => setNewNotes(e.target.value)} placeholder="Optional…" className="bg-ink-800 border-ink-700 mt-2" data-testid="new-session-notes" />
            </div>
            <Button type="submit" className="w-full bg-burgundy hover:bg-burgundy-hover" data-testid="new-session-submit">Create Session</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryPill({ label, value, color }) {
  const map = {
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    amber:   "bg-amber-500/10 text-amber-300 border-amber-500/30",
    blue:    "bg-blue-500/10 text-blue-300 border-blue-500/30",
    red:     "bg-red-500/10 text-red-300 border-red-500/30",
    ink:     "bg-ink-800 text-ink-300 border-ink-600",
  };
  return (
    <div className={`inline-flex items-center gap-2 border rounded px-2.5 py-1 ${map[color]}`}>
      <span className="text-[10px] uppercase tracking-wider font-bold">{label}</span>
      <span className="font-display font-black text-lg">{value}</span>
    </div>
  );
}
